import {
  type OperationType,
  AuthStateActionType,
  HttpMethod,
  AIActionModel as PrismaAIActionModel,
  AIAssertionModel as PrismaAIAssertionModel,
  AIAssertionScreenshotType as PrismaAIAssertionScreenshotType,
  AIExtractionModel as PrismaAIExtractionModel,
  AIExtractionMethod as PrismaAIExtractionMethod,
  ValueType as PrismaValueType,
  SpecialLoginType,
} from '@prisma/client';
import { z } from 'zod';
import { actionMethods, assertionMethods } from '~/ai_tools/playwright_utils/types';
import { zHttpHeaderKey } from '~/common/http/validation';
import { CONDITIONAL_PROMPT_MAX_LENGTH } from '~/common/operation/constants';
import { zExtractionType } from '~/common/stably-email/stably-email-types';
import { zVariables } from '~/types';

const zTarget = z.object({
  name: z.string(),
  locator: z.string(),
  boundingBox: z
    .object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
});
export type TargetSchema = z.infer<typeof zTarget>;

export const zValueType = z.enum(PrismaValueType);
export type ValueType = z.output<typeof zValueType>;

export const zAIAssertionScreenshotType = z.enum(PrismaAIAssertionScreenshotType);
export type AIAssertionScreenshotType = z.infer<typeof zAIAssertionScreenshotType>;

export const zAIAssertionModel = z.enum(PrismaAIAssertionModel);
export type AIAssertionModel = z.infer<typeof zAIAssertionModel>;

export const zAIActionModel = z.enum(PrismaAIActionModel);
export type AIActionModel = z.infer<typeof zAIActionModel>;

export const zAIExtractionModel = z.enum(PrismaAIExtractionModel);
export type AIExtractionModel = z.infer<typeof zAIExtractionModel>;

export const zAIExtractionMethod = z.enum(PrismaAIExtractionMethod);
export type AIExtractionMethod = z.infer<typeof zAIExtractionMethod>;

/**
 * Validates if the extraction method and model combination is valid
 * ARIA_SNAPSHOT only supports STABLY_FLASH and BACKUP_MODEL
 * SCREENSHOT and SCREENSHOT_AND_ARIA support all models
 */
export function isValidExtractionMethodModelCombination(method: AIExtractionMethod, model: AIExtractionModel): boolean {
  if (method === 'ARIA_SNAPSHOT') {
    return model === 'STABLY_FLASH' || model === 'BACKUP_MODEL';
  }
  return true;
}

export const createZodBasicOperationSchema = <T extends z.ZodType>(zodTargetSchema: T) => {
  return z.discriminatedUnion('operationType', [
    z.object({
      operationType: z.literal('NAVIGATION' satisfies OperationType),
      value: z.string(),
      valueType: zValueType.default('STATIC'),
      newPage: z.boolean().optional(),
      options: z.record(z.string(), z.any()).optional(),
    }),
    z.object({
      operationType: z.literal('WAIT' satisfies OperationType),
      timeoutInMillis: z.number(),
    }),
    z.object({
      operationType: z.literal('ACTION' satisfies OperationType),
      action: z.enum(actionMethods),
      target: zodTargetSchema,
      // Used for drag and drop
      target2: zodTargetSchema.optional(),
      value: z.string().optional(),
      valueType: zValueType.default('STATIC'),
      options: z.record(z.string(), z.any()).optional(),
    }),
    z.object({
      operationType: z.literal('ASSERTION' satisfies OperationType),
      assertion: z.enum(assertionMethods),
      target: zodTargetSchema,
      invert: z.boolean().optional(),
      value: z.string().optional(),
      valueType: zValueType.default('STATIC'),
      options: z.record(z.string(), z.any()).optional(),
    }),
    z.object({
      operationType: z.literal('AI_ASSERTION' satisfies OperationType),
      assertion: z.string(),
      screenshotType: zAIAssertionScreenshotType,
      includeScreenshot: z.boolean().default(true),
      model: zAIAssertionModel,
    }),
  ]);
};

export const zAiAssertionOp = z.object({
  operationType: z.literal('AI_ASSERTION' satisfies OperationType),
  assertion: z.string(),
  screenshotType: zAIAssertionScreenshotType,
  includeScreenshot: z.boolean().default(true),
  model: zAIAssertionModel,
});

export const zAiActionOp = z.object({
  operationType: z.literal('AI_ACTION' satisfies OperationType),
  prompt: z.string(),
  model: zAIActionModel,
});

export const zCodeOp = z.object({
  operationType: z.literal('CODE_BLOCK' satisfies OperationType),
  code: z.string(),
  description: z.string().optional(),
});

export const zUploadOp = z.object({
  operationType: z.literal('UPLOAD' satisfies OperationType),
  files: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      storageKey: z.string(),
    }),
  ),
});

const zDefineVariableOp = z.object({
  operationType: z.literal('DEFINE_VARIABLE' satisfies OperationType),
  name: z.string(),
  value: z.string(),
  type: zValueType,
  includeScreenshot: z.boolean().optional(),
  extractionModel: zAIExtractionModel.default('STABLY_FLASH'),
  extractionMethod: zAIExtractionMethod.default('ARIA_SNAPSHOT'),
});

const zVariableExtraction = z.object({
  variableName: z.string(),
  extractionType: zExtractionType,
  customExtractionPrompt: z.string().optional(),
});

export type EmailVariableExtraction = z.infer<typeof zVariableExtraction>;

const zEmailFilters = z.object({
  sender: z.string().optional(),
  subject: z.string().optional(),
});

export type EmailFilters = z.infer<typeof zEmailFilters>;

export const zReceiveEmailOp = z.object({
  operationType: z.literal('RECEIVE_EMAIL' satisfies OperationType),
  toEmailAddressValue: z.string(),
  toEmailAddressType: zValueType,
  variableExtraction: zVariableExtraction.optional(),
  filters: zEmailFilters.optional(),
});

const zFetchRequestHeaders = z.record(zHttpHeaderKey, z.string());
export type FetchRequestHeaders = z.infer<typeof zFetchRequestHeaders>;

export const zFetchRequestOp = z.object({
  operationType: z.literal('FETCH_REQUEST' satisfies OperationType),
  responseVariableName: z.string().optional(),
  request: z.object({
    url: z.string(),
    method: z.enum(HttpMethod),
    headers: zFetchRequestHeaders.optional(),
    body: z.string().optional(),
  }),
});

export const zAuthStateOp = z.object({
  operationType: z.literal('AUTH_STATE' satisfies OperationType),
  actionType: z.enum(AuthStateActionType),
  testAccountId: z.string(),
});

export const zSpecialLoginOp = z.object({
  operationType: z.literal('SPECIAL_LOGIN' satisfies OperationType),
  type: z.enum(SpecialLoginType),
  testAccountId: z.string(),
});

export const zComponentCallOp = z.object({
  operationType: z.literal('COMPONENT_CALL' satisfies OperationType),
  componentId: z.string(),
  componentName: z.string().optional(),
  inputParameters: zVariables.optional(),
});

export const zConditionalOp = z.object({
  operationType: z.literal('CONDITIONAL' satisfies OperationType),
  conditionPrompt: z.string().max(CONDITIONAL_PROMPT_MAX_LENGTH),
  conditionalBlockId: z.string(),
});

export const zEndTestOp = z.object({
  operationType: z.literal('END_TEST' satisfies OperationType),
  passTest: z.boolean(),
});

const zConditionalBlock = z.object({
  conditionalBlockId: z.string(),
  blockType: z.enum(['then', 'else']),
});
export type ConditionalBlock = z.infer<typeof zConditionalBlock>;

// We split this off for re-use reasons
export const zOperationBasicFields = z.object({
  operationId: z.string().optional(),
  skip: z.boolean().optional(),
});

// Union for the parent to add this operation to (we only need the direct parent, so it should be one of the following)
// All nested operations (under nested components) belong to a top level stepId but NOT connected in the database
// componentId is specified to indicate that we're editing a nested operation, and therefore DONT connect to the top level stepId
// see opModelToPrismaOpUpdate for more details on how componentId is used
// operation can also be within a step (no parent) - this parent context will be undefined in that case.
export const zCreateOperationParentContext = z.intersection(
  z.object({
    order: z.number(),
    rootOperationId: z.string(),
  }),
  z.union([
    // Operation within a component
    z.object({
      componentId: z.string(),
      parameters: z.array(z.string()).optional(),
      conditional: z.undefined(),
    }),
    // Operation within a conditional block
    z.object({
      componentId: z.undefined(),
      conditional: zConditionalBlock,
    }),
  ]),
);

export type OperationParentContext = z.infer<typeof zCreateOperationParentContext>;

const createZodOperationSchema = <T extends z.ZodType>(targetSchema: T) => {
  return zOperationBasicFields.extend({
    // Order & stepId are required if operationId is not provided
    order: z.number().optional(),
    stepId: z.string().optional(),

    parentContext: zCreateOperationParentContext.optional(),

    pageAlias: z.string(),
    opensPageAlias: z.string().optional(),
    opensFileUploadDialog: z.string().optional(),
    operation: z.union([
      createZodBasicOperationSchema(targetSchema),
      zCodeOp,
      zUploadOp,
      zAiAssertionOp,
      zAiActionOp,
      zDefineVariableOp,
      zReceiveEmailOp,
      zFetchRequestOp,
      zAuthStateOp,
      zSpecialLoginOp,
      zComponentCallOp,
      zConditionalOp,
      zEndTestOp,
    ]),
  });
};

export const zOperation = createZodOperationSchema(zTarget.optional());

export const zExecutionTarget = zTarget.extend({
  elementTagName: z.string().optional(),
  outerHtml: z.string().optional(),
});
export type ExecutionTarget = z.infer<typeof zExecutionTarget>;
export const zExecutionOperation = createZodBasicOperationSchema(zExecutionTarget.optional());
export type ExecutionOperation = z.output<typeof zExecutionOperation>;

export type OperationData = z.input<typeof zOperation>;

const _zAiAddedOperation = createZodOperationSchema(zExecutionTarget.optional());

/**
 * Represents an operation that has been processed and added by AI.
 * Requires operationId (unlike OperationData where it's optional) since
 * this represents an operation that has already been created/stored.
 */
export type AiAddedOperation = z.input<typeof _zAiAddedOperation> & {
  // Overwride these fields
  operationId: string;
  // Action screenshots include visual element highlighting (drawing outlines around interacted elements),
  // which requires additional image processing and impacts performance, especially on mobile devices
  // So we return a promise that can be awaited or not
  actionScreenshotPromise?: Promise<Buffer>;
  beforeScreenshot?: Buffer;
  afterScreenshot?: Buffer;
};
